import fileDownload from 'js-file-download';
import { ref, toValue, type MaybeRefOrGetter } from 'vue';
import { useI18n } from 'vue-i18n';
import { useNotifications } from '../stores/notificationStore';

export interface FileDownloadOptions {
  data?: string | ArrayBuffer | ArrayBufferView<ArrayBufferLike> | Blob;
  fileName?: string;
  mime?: string;
}

export type UseFileDownloadOptions = {
  [K in keyof FileDownloadOptions]: MaybeRefOrGetter<FileDownloadOptions[K]>;
};

export const useFileDownload = ({
  data,
  fileName = 'download.txt',
  mime = 'text/plain',
}: UseFileDownloadOptions) => {
  const { t } = useI18n();
  const { promiseNotification } = useNotifications();

  const isDownloading = ref(false);

  const downloadAsFile = async (
    options?:
      | FileDownloadOptions
      | (() => Promise<string | ArrayBuffer | ArrayBufferView<ArrayBufferLike> | Blob>),
  ) => {
    const downloadData = toValue(
      typeof options === 'function' ? await options() : (options?.data ?? data),
    );
    const downloadFileName = toValue(
      typeof options === 'function' ? fileName : (options?.fileName ?? fileName),
    )!;
    const downloadMime = toValue(typeof options === 'function' ? mime : (options?.mime ?? mime));

    if (!downloadData) {
      throw new Error('No data provided for file download.');
    }

    fileDownload(downloadData, downloadFileName, downloadMime);
  };

  /**
   * Exports can take a while server side, so the user gets a pending
   * notification naming the file right away, which then turns into the success
   * or failure result instead of stacking up a second toast.
   */
  const downloadResponseAsFile = async <
    TStatus extends number,
    TData,
    TResponse extends { data: TData; status: TStatus },
    TSuccessStatus extends TStatus = 200 & TStatus,
  >(
    response: Promise<TResponse>,
    successStatus: TSuccessStatus = 200 as TSuccessStatus,
    options: Omit<FileDownloadOptions, 'data'> = {},
  ) => {
    const downloadFileName = toValue(options.fileName ?? fileName)!;

    isDownloading.value = true;

    const download = (async () => {
      const { data, status } = await response;

      if (status !== successStatus) {
        throw new Error(
          `Failed to download file. Expected status ${successStatus}, but got ${status}.`,
        );
      }

      const content =
        data instanceof Blob || ArrayBuffer.isView(data) || typeof data === 'string'
          ? data
          : JSON.stringify(data);

      await downloadAsFile({ ...options, data: content });
    })();

    try {
      await promiseNotification(
        download,
        {
          title: t('notification.download.progress.title'),
          message: t('notification.download.progress.message', { fileName: downloadFileName }),
          progress: -1,
        },
        () => ({
          type: 'success',
          title: t('notification.download.complete.title'),
          message: t('notification.download.complete.message', { fileName: downloadFileName }),
          progress: null,
        }),
        () => ({
          type: 'error',
          title: t('notification.download.failed.title'),
          message: t('notification.download.failed.message', { fileName: downloadFileName }),
          progress: null,
        }),
      );

      return true;
    } catch {
      // The failure notification above is the user facing result - callers just
      // trigger the download and never handle the rejection themselves.
      return false;
    } finally {
      isDownloading.value = false;
    }
  };

  return { downloadAsFile, downloadResponseAsFile, isDownloading };
};
